<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-card>
          <v-row no-gutters align="center">
            <v-col>
              <v-card-title>Case Manager</v-card-title>
              <v-card-subtitle>{{ getOrg }}</v-card-subtitle>
            </v-col>
            <v-col align="right">
              <v-btn class="ma-3" fab dark x-small color="blue" @click="onAddClick()">
                <v-icon dark>mdi-plus</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- filters -->
    <v-expansion-panels class="mt-3">
      <v-expansion-panel>
        <v-expansion-panel-header><span class="text-subtitle-1"><v-icon>mdi-magnify</v-icon>Search and Filter</span></v-expansion-panel-header>
        <v-expansion-panel-content>
          <!-- Row 1 -->
          <v-row dense align="center">
            <!-- Name -->
            <v-col cols="3">
              <v-text-field label="Case Name" v-model="name" dense clearable />
            </v-col>
            <!-- Country -->
            <v-col cols="3">
              <v-autocomplete
                    v-model="countryFilter"
                    :items="countries"
                    item-text="name"
                    item-value="alpha2"
                    dense
                    label="Country"
                    multiple
                    clearable
                    @input="searchInputCountry=null"
                    :search-input.sync="searchInputCountry"
              ></v-autocomplete>
            </v-col>
            <!-- Type -->
            <v-col cols="2">
              <v-select dense :label="$t('label.type')" v-model="type" :items="types" clearable />
            </v-col>
            <v-col cols="3">
              <!-- Last screen date -->
              <v-menu ref="screeningDateRange" v-model="screeningDateRange" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="dateRangeFormattedComputed" label="Last Screening Date" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" dense clearable @click:clear="clearScreeningDates"></v-text-field>
                </template>
                <v-date-picker v-model="dates" range no-title>
                  <v-spacer />
                  <v-btn text color="primary" @click="screeningDateRange = false">
                    Cancel
                  </v-btn>
                  <v-btn text color="primary" @click="$refs.screeningDateRange.save(dates)">
                    OK
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <!-- Row 2  -->
          <v-row dense align="center">
            <!-- Local ID  -->
            <v-col cols="2">
              <v-text-field label="Local ID" v-model="localId" dense clearable />
            </v-col>
            <!-- Case Group -->
            <v-col cols="2">
              <v-select dense label="Group" v-model="caseGroups" :items="getCaseGroups" item-text="name" return-object clearable multiple/>
            </v-col>
            <!-- Validation status -->
            <v-col cols="3">
              <div class="d-flex">
                <v-combobox v-model="vatStatus" :items="vatStatuses" label="Validation Statuses" multiple dense clearable />
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon color="primary" v-bind="attrs" v-on="on" class="px-2 pb-1">
                      help
                    </v-icon>
                  </template>
                  <span>Filter by status of additional case validations.</span>
                </v-tooltip>
              </div>
            </v-col>
          </v-row>

          <!-- Client defined case labels -->
          <v-row dense align="center" v-if="getAccountSettings.customLabels">
            <v-col 
              v-for="(value, labelName) in getAccountSettings.customLabels"
              :key="labelName">
              <v-select
                :items=value
                :label=labelName
                v-model="labelFilter[labelName]"
                multiple
                clearable
                dense
              ></v-select>
            </v-col>
          </v-row>

          <!-- Search -->
          <v-row dense align="center">
            <!-- View archived -->
            <v-col cols="3" class="d-flex">
              <v-switch height="1" inset dense v-model="viewArchived" label="View Archived" color="blue" />
            </v-col>
            <v-spacer />
            <v-col align="right" cols="1">
              <v-btn fab dark x-small color="blue" @click="getCases()">
                <v-icon dark>mdi-magnify</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- table -->
    <v-row dense class="mt-3">
      <v-col>
        <v-data-table :headers="headers" :items="cases" @toggle-select-all="selectAll" @item-selected="itemSelected" :options.sync="options" :server-items-length="totalCases" :footer-props="footerProps" v-model="selectedCases" item-key="id" :loading="loading" show-select no-data-text="No cases">
          <template v-slot:top>
            <v-toolbar flat>
              <v-spacer />
              <v-menu offset-y max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn color="primary" dark v-bind="attrs" v-on="on">
                    Actions
                    <v-icon dark right>mdi-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <!-- run search -->
                  <v-list-item link @click="onScreenClick(selectedCases)" :disabled="selectedCases.length < 1">
                    <v-list-item-icon>
                      <v-icon>mdi-magnify</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Screen Selected</v-list-item-title>
                      <v-list-item-subtitle>
                        Screens selected cases
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <!-- archive -->
                  <v-list-item link @click="onArchiveClick({ archive: true })" :disabled="selectedCases.length < 1">
                    <v-list-item-icon>
                      <v-icon>mdi-delete</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="red--text">Archive Selected</v-list-item-title>
                      <v-list-item-subtitle class="text-wrap">
                        Archives selected cases. They will no longer be
                        displayed cases table or be included in ongoing
                        screening. They will remain in history
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <!-- bulk group assign/unassign -->
                  <!-- CSV Export -->
                  <v-list-item link @click="onCsvExport()">
                    <v-list-item-icon>
                      <v-icon> mdi-download </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Export Case Information</v-list-item-title>
                      <v-list-item-subtitle>Export a CSV of Case information based on current search
                        parameters</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-toolbar>
          </template>

          <template v-slot:item.name="{ item }">
            <span v-if="!item.archived">{{ item.name }}</span>
            <span v-else class="font-italic text--disabled">
              {{ item.name }}
            </span>
          </template>

          <template v-slot:item.type="{ item }">
            <span v-if="item.type">
              <span v-if="'individual'.includes(item.type.toLowerCase())">
                <v-icon small>fa-user</v-icon>
              </span>

              <span v-else-if="'entity'.includes(item.type.toLowerCase())">
                <v-icon small>fa-building</v-icon>
              </span>

              <span v-else-if="'vessel'.includes(item.type.toLowerCase())">
                <v-icon small>fa-ship</v-icon>
              </span>
            </span>
            <span v-else>

            </span>
          </template>
          <template v-slot:item.caseGroupId="{ item }">
            {{
            item.caseGroupId == null || item.caseGroupId === undefined
            ? ''
            : getCaseGroupName(item.caseGroupId)
            }}
          </template>
          <template v-slot:item.createdOn="{ item }">
            {{ formatDate(item.createdOn) }}
          </template>

          <template v-slot:item.lastScreenDate="{ item }">
            {{ formatDateTime(item.lastScreenDate) }}
          </template>

          <template v-slot:item.idStatus="{ item }">
            <span v-if="!item.identification || item.identification.length == 0">
              <!-- nothing -->
            </span>
            <span v-else-if="item.identification && hasInvalidId(item.identification)">
              <v-icon small>fa-error</v-icon>
            </span>
            <span v-else-if="item.identification && hasPendingId(item.identification)">
              <v-icon small>fa-clock</v-icon>
            </span>

            <span v-else>
              <v-icon small>fa-check</v-icon>
            </span>
          </template>

          <template v-slot:item.controls="{item, index}">
            <v-row dense>
              <v-col>
                <v-btn icon x-small color="green" :disabled="item.archived" @click="onViewClick(item, index)" :loading="viewBtnLoading[index]">
                  <v-icon dark>mdi-magnify</v-icon>
                </v-btn>
              </v-col>
              <!-- <v-col>
                <v-btn icon x-small color="black">
                  <v-icon dark>mdi-file-pdf-box</v-icon>
                </v-btn>
              </v-col> -->
              <v-col>
                <v-btn icon x-small color="blue" :disabled="item.archived" @click="onEditClick(item)">
                  <v-icon dark>mdi-pencil</v-icon>
                </v-btn>
              </v-col>
              <v-col v-if="!item.archived">
                <v-btn icon x-small color="red" @click="onArchiveClick({ caseId: item.id, archive: true })">
                  <v-icon dark title="Archive">mdi-delete</v-icon>
                </v-btn>
              </v-col>

              <v-col v-else>
                <v-btn icon x-small color="green" @click="onArchiveClick({ caseId: item.id, archive: false })">
                  <v-icon dark title="Unarchive">mdi-delete</v-icon>
                </v-btn>
              </v-col>

            </v-row>
          </template>


        </v-data-table>
      </v-col>
    </v-row>

    <AddCase :visible="isAddDialogVisible" v-on:onConfirmed="onAddCaseConfirmed" v-on:onCancelled="onAddCaseClose" :caseGroups="getCaseGroups" />

    <EditCase v-if="selectedCase" :selectedCase="selectedCase" :visible="isEditDialogVisible" :caseGroups="getCaseGroups" v-on:onConfirmed="onEditCaseClose" v-on:onCancelled="onEditCaseClose" />

    <ViewCase v-if="selectedCase" :selectedCase="selectedCase" :selectedCaseHistory="selectedCaseHistory" :visible="isViewDialogVisible" :showVatStatus="getCaseGroupVatEnabled(selectedCase.caseGroupId)" :showTinStatus="getCaseGroupTinEnabled(selectedCase.caseGroupId)" v-on:onCancelled="onViewCaseClose" />

    <Confirm ref="confirm" />

  </v-container>
</template>

<script>
import axios from "axios";
import { mapGetters, mapActions } from "vuex";
import {
  formatDate,
  formatDateTime,
  dateRangeFormatted,
} from "../utils/dateUtils";
import { getCaseGroupName, getCaseGroupVatEnabled, getCaseGroupTinEnabled } from "../utils/caseGroupUtils";
import AddCase from "../modals/case/AddCase";
import EditCase from "../modals/case/EditCase";
import ViewCase from "../modals/case/ViewCase";
import Confirm from "../modals/Confirm";
import { getHistory } from "../utils/caseUtils"
import countriesjson from "../assets/countries.json"
export default {
  name: "CaseManager",
  components: {
    AddCase,
    EditCase,
    ViewCase,
    Confirm,
  },
  data() {
    return {
      loading: false,
      modalLoading: false,

      // search
      localId: "",
      name: "",
      countries: countriesjson,
      countryFilter: [],
      labelFilter: {},
      searchInputCountry: null,
      caseGroups: [],
      type: "",
      viewArchived: false,
      dates: [],
      vatStatus: [],

      // pagination
      totalCases: 0,
      options: {},
      footerProps: { 
        "items-per-page-options": [10, 20, 50,200],
        "show-first-last-page": true
      },
      allSelected: false,

      screeningDateRange: false,
      selectedCase: null,
      selectedCaseHistory: null,
      selectedCases: [],
      isAddDialogVisible: false,
      isEditDialogVisible: false,
      isViewDialogVisible: false,
      viewBtnLoading: {},
      error: "",
      types: ["Individual", "Entity", "Vessel"],
      vatStatuses: ["Valid", "Invalid", "Unverified"],
      headers: [
        { text: "Name", value: "name" },
        { text: "Local ID", value: "localId", width: 200},
        // { text: "ID", value: "id" },
        { text: "Type", value: "type"}, // Should be an icon (person, building, group, vessel)
        //{ text: "ID Status", value: "idStatus"},
        // { text: "Business", value: "Business" },
        // { text: "Region", value: "Region" },
        // { text: this.$t("view.caseManager.table.caseGroups"), value: "groups" },
        { text: "Group", value: "caseGroupId" },
        { text: "Last Screening", value: "lastScreenDate" },
        { text: "Created", value: "createdOn" },
        {
          text: "Admin",
          value: "controls",
          sortable: false,
          align: "right",
          width: 140,
        }
      ]
    };
  },
  computed: {
    ...mapGetters(["getOrg", "getCaseGroups", "getAccountSettings"]),
    cases() {
      return this.$store.getters.getCases;
    },
    dateRangeFormattedComputed: {
      get: function () {
        return this.dateRangeFormatted(this.dates[0], this.dates[1]);
      },
      set: function (newDates) {
        newDates;
        //this.dates = [newDates];
      },
    },
  },
  watch: {
    options: {
      handler() {
        this.getCases();
      },
      deep: true,
    },
  },
  methods: {
    formatDate,
    formatDateTime,
    dateRangeFormatted,
    getCaseGroupName,
    getCaseGroupVatEnabled,
    getCaseGroupTinEnabled,
    getHistory,
    ...mapActions(["fetchCaseGroups", "setGlobalAlert", "patchCases"]),
    /**
     * Created() calls refresh if cases are not initialized
     * Refesh() calls fetchGroupsAndCases() which turns off loading regardless with finally
     * Any error from fetchGroupsAndCases() bubbles up to created() which catches
     * The order of fetchGroupsAndCases action calls matters because table refreshes on setting of cases, which causes a call to getCaseGroupName()
     * TODO consider moving getCaseGroupName() to module as it might be needed elsewhere
     */
    // async refresh() {
    //   this.loading = true;
    //   await this.fetchGroupsAndCases().finally(() => {
    //     this.loading = false;
    //   });
    // },
    // async fetchGroupsAndCases() {
    //   await this.fetchCaseGroups();
    //   await this.getCases();
    // },



    onCsvExport() {
      if(this.totalCases > 5000){
        this.setGlobalAlert({
          alert: true,
          color: "blue",
          alertMessage: "Please narrow your search criteria to proceed with CSV export.",
        });
        return;
      }

      // Check table total and alert on limit - check if history does this. 

      var params = {};
      if (this.localId) params.localId = this.localId;
      if (this.name) params.name = this.name;
      if (this.caseGroups){
        var groupIds = []
        for(let cG in this.caseGroups){
          groupIds.push(this.caseGroups[cG].id)
        }
        params.groupId = groupIds.join(",");
      } 
      if (this.type) params.type = this.type;
      if (this.vatStatus) {
        params.vatStatus = this.vatStatus.join(",").toLowerCase();
      }
      if (this.viewArchived) params.archived = true;
      if (this.dates[0]) params.screenedAfter = this.dates[0];
      if (this.dates[1]) params.screenedBefore = this.dates[1];
      if (this.countryFilter){
        params.countries = this.countryFilter.join(",");
      }
      
      //api/reports/csv/cases
      let routeData = this.$router.resolve({
        name: "casesCsv",
        query: params,
      });
      const link = document.createElement("a");
      link.href = routeData.href;
      link.click();

    },



    selectAll: function (event) {
      this.allSelected = event.value;
    },
    itemSelected: function () {
      this.allSelected = false;
    },
    clearScreeningDates() {
      this.dates = [];
    },
    onAddClick: function () {
      this.isAddDialogVisible = true;
    },
    onEditClick: function (selectedCase) {
      this.selectedCase = selectedCase;
      this.isEditDialogVisible = true;
    },
    onViewClick: function (selectedCase, index) {
      this.$set(this.viewBtnLoading, index, true)
      this.getHistory(selectedCase.id)
        .then((res) => {
          this.selectedCase = selectedCase;
          this.selectedCaseHistory = res;
          this.isViewDialogVisible = true;
        })
        .catch((error) => {
          if(error.response.status === 404){
            // 404 is acceptable, and expected for new case records that have not been screened yet. 
            this.selectedCase = selectedCase;
            this.selectedCaseHistory = {};
            this.isViewDialogVisible = true;
          } else {
            this.setGlobalAlert({
              alert: true,
              color: "red",
              alertMessage: "Error opening case, please try again.",
            });
          }
          
        })
        .finally(() => {
          this.viewBtnLoading = {}
        })
    },
    /* Used to switch between associations in Case view */
    // onSwitchCase: function (switchCase) {
    //   this.selectedCase = null;
    //   this.isViewDialogVisible = false;
    //   this.onViewClick(switchCase);
    // },
    onAddCaseConfirmed() {
      this.isAddDialogVisible = false;
      this.getCases();
    },
    onAddCaseClose() {
      this.isAddDialogVisible = false;
    },
    onEditCaseClose() {
      this.isEditDialogVisible = false;
      this.selectedCase = null; // Destroys the component in order to re call created() to init attributes
    },
    onViewCaseClose() {
      this.isViewDialogVisible = false;
      this.selectedCase = null; // Destroys the component in order to re call created() to init attributes
    },
    onArchiveClick: function ({ caseId, archive }) {

      var params = { archive: archive }
      var total;

      if (caseId != null) {
        total = 1;
        params.ids = [caseId];
      } else {
        total = this.allSelected ? this.totalCases : this.selectedCases.length;
      }

      if (archive) {
        this.$refs.confirm
          .open(
            "Archive " + total + " cases?",
            "Archiving cases excludes them from scheduled recurring screening and they will not be visible in this table.",
            { color: "red" }
          )
          .then((confirm) => {
            if (confirm) {
              this.callPatchCasesForFilter(params);
            }
          });
      } else {
        this.callPatchCasesForFilter(params);
      }
    },
    callPatchCasesForFilter: function (patch) {
      this.loading = true;

      var params = {};

      if (this.allSelected) {
        if (this.localId) params.localId = this.localId;
        if (this.name) params.name = this.name;
        if (this.caseGroup) params.groupId = this.caseGroup.id;
        if (this.type) params.type = this.type;
        // if (this.viewArchived) params.archived = true; // TODO This may not be needed. Test with bulk archive. 
        if (this.dates[0]) params.screenedAfter = this.dates[0];
        if (this.dates[1]) params.screenedBefore = this.dates[1];
      } else {
        if (!patch.ids) {
          patch.ids = this.selectedCases.map(function (aCase) {
            return aCase.id;
          });
        }
      }

      this.patchCases({ params: params, patch: patch })
        .then((modified) => {
          this.selectedCases = [];
          this.getCases();
          this.setGlobalAlert({
            alert: true,
            color: "green",
            alertMessage: modified + " cases updated",
          });
        })
        .catch(() => {
          this.setGlobalAlert({
            alert: true,
            color: "red",
            alertMessage: "Error updating cases, please try again.",
          });
          this.loading = false;
        })
    },
    hasAttribute(attribute, aCase){
      if(aCase.caseAttributes && Object.hasOwn(aCase.caseAttributes, attribute)){
        return true;
      }
      return false;
    },
    onScreenClick: function (selectedCases) {
      this.loading = true;
      var request = {};
      request.cases = selectedCases;
      axios
        .post("/api/search/", request)
        .then(() => {
          this.getCases(); // TODO is this needed?
          this.selectedCase = {};
          this.setGlobalAlert({
            alert: true,
            color: "green",
            alertTitle: "Screening " + selectedCases.length + " cases.",
            alertMessage: "Case reports will be available shortly.",
          });
        })
        .catch(() => {
          this.loading = false; // Turn off loading here, otherwise it's turned off in getCases
          this.setGlobalAlert({
            alert: true,
            color: "red",
            alertTitle: "Oops.",
            alertMessage: "Unable to perform search. Please try again",
          });
        })
        .finally(() => {
          this.selectedCase = [];
        });
    },
    hasInvalidId(identification){
      for(let id in identification){
        if(identification[id].status && identification[id].status == 'INVALID'){
          return true;
        }
      }
    },
    hasPendingId(identification){
      for(let id in identification){
        if(!identification[id].status || (identification[id].status && identification[id].status == 'UNVERIFIED')){
          return true;
        }
      }
    },
    getCases() {
      this.loading = true;

      var params = {};
      params.limit = this.options.itemsPerPage;
      params.page = this.options.page - 1; // API expects 0 index

      if(this.options.sortBy[0]){
        params.sortBy = this.options.sortBy[0];
        params.sortDesc = this.options.sortDesc[0]
      }

      if (this.localId) params.localId = this.localId;
      if (this.name) params.name = this.name;
      if (this.caseGroups){
        var groupIds = []
        for(let cG in this.caseGroups){
          groupIds.push(this.caseGroups[cG].id)
        }
        params.groupId = groupIds.join(",");
      } 
      if (this.type) params.type = this.type;
      if (this.vatStatus) {
        params.vatStatus = this.vatStatus.join(",").toLowerCase();
      }
      if (this.viewArchived) params.archived = true;
      if (this.dates[0]) params.screenedAfter = this.dates[0];
      if (this.dates[1]) params.screenedBefore = this.dates[1];
      if (this.countryFilter){
        params.countries = this.countryFilter.join(",");
      }

      /**
       * Label filter
       */
      params.labels = this.labelFilter;

      this.$store
        .dispatch("fetchCases", { id: null, params: params })
        .then((totalCases) => {
          this.totalCases = totalCases;
        })
        .catch((error) => {
          if (401 != error.response.status) {
            console.error("Error fetching cases.", error);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  async created() {
    if(this.getAccountSettings && this.getAccountSettings.customLabels){
      var headerIndex = 2; // Add after name, ID, etc. 
      for(const key in this.getAccountSettings.customLabels){
        if(Object.prototype.hasOwnProperty.call(this.getAccountSettings.customLabels, key)){
          this.headers.splice(headerIndex, 0, {text: key, value: "customLabels." + key});
          headerIndex++;
        }
      }
    } 

    if (!this.$store.state.caseGroups.length) {
      this.fetchCaseGroups().catch((e) => {
        console.error("Error getting groups: " + e);
        this.setGlobalAlert({
          alert: true,
          color: "red",
          alertTitle: null,
          alertMessage: this.$t("error.caseGroups.error"),
        });
      });
    }
  },
};
</script>
  